import { useEffect, useRef, useState, ElementType } from 'react'
import { useNavigate } from 'react-router-dom'
import Lottie, { LottieRefCurrentProps } from 'lottie-react'
import { Circle, X, ArrowRight, ChevronDown, DollarSign, Palette, Users, Upload, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/store/auth-store'
import { useUIStore } from '@/store/ui-store'
import {
  getOnboardingStatus,
  dismissOnboarding,
  isOnboardingDismissed,
  dismissKey,
  introSeenKey,
  OnboardingStatus,
} from '@/services/onboarding-service'

// Local Lottie JSON files — place these in paver-frontend/public/animations/
const ANIMATION_URLS: Record<keyof OnboardingStatus, string> = {
  pricingComplete:      '/animations/pricing.json',
  brandingComplete:     '/animations/branding.json',
  usersInvited:         '/animations/team.json',
  customersAdded:       '/animations/customers.json',
  quickbooksConnected:  '/animations/quickbooks.json',
}

const CELEBRATION_URL = '/animations/celebration.json'

// Fallback lucide icons if Lottie fails to load
const FALLBACK_ICONS: Record<keyof OnboardingStatus, ElementType> = {
  pricingComplete:     DollarSign,
  brandingComplete:    Palette,
  usersInvited:        Users,
  customersAdded:      Upload,
  quickbooksConnected: Link2,
}

/** Fetches a Lottie JSON from a URL. Returns null if unavailable. */
function useLottieData(url: string) {
  const [data, setData] = useState<unknown>(null)
  useEffect(() => {
    let cancelled = false
    fetch(url)
      .then((r) => r.json())
      .then((json) => { if (!cancelled) setData(json) })
      .catch(() => { /* fall through to lucide fallback */ })
    return () => { cancelled = true }
  }, [url])
  return data
}

interface ChecklistItem {
  key: keyof OnboardingStatus
  label: string
  description: string
  route: string
  required: boolean
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    key: 'pricingComplete',
    label: 'Review Your Pricing',
    description: "We've loaded a starter price list so you can begin estimating right away — review the rates and swap in your own so estimates match your real numbers.",
    route: '/pricing/materials',
    required: false,
  },
  {
    key: 'brandingComplete',
    label: 'Set Up Brand & Proposal Design',
    description: 'Upload your logo and pick your brand colors so proposals look polished and professional.',
    route: '/settings/company',
    required: true,
  },
  {
    key: 'usersInvited',
    label: 'Invite Your Team',
    description: 'Add your estimators and managers so everyone can collaborate on bids.',
    route: '/settings/users',
    required: false,
  },
  {
    key: 'customersAdded',
    label: 'Import Your Customers',
    description: 'Bring in your existing customer list so you can attach them to estimates right away.',
    route: '/customers/import',
    required: false,
  },
  {
    key: 'quickbooksConnected',
    label: 'Connect QuickBooks',
    description: 'Sync invoices and payments directly to QuickBooks Online to keep your books current.',
    route: '/settings/company',
    required: false,
  },
]

/** Filled-circle checkmark used for completed steps in both render modes. */
function CheckMark({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-primary shrink-0">
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path d="M5.5 10.5L8.5 13.5L14.5 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function OnboardingChecklist({ onUnlocked, onDismissed }: { onUnlocked: () => void; onDismissed: () => void }) {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const navigate = useNavigate()
  const [status, setStatus] = useState<OnboardingStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)
  const [completing, setCompleting] = useState(false)
  const celebrationData = useLottieData(CELEBRATION_URL)

  useEffect(() => {
    if (!user?.tenantId) return
    // Server value wins; stale localStorage `true` is purged inside the helper (#27).
    if (isOnboardingDismissed(user)) {
      setDismissed(true)
      setLoading(false)
      return
    }
    const tenantId = user.tenantId
    getOnboardingStatus()
      .then((s) => {
        setStatus(s)
        const requiredDone = CHECKLIST_ITEMS.filter((i) => i.required).every((i) => s[i.key])
        const allDone = CHECKLIST_ITEMS.every((i) => s[i.key])
        if (!requiredDone) return
        onUnlocked()
        if (allDone) {
          // The only celebration left: all five steps genuinely complete.
          setCompleting(true)
          setExpanded(true)
        } else if (localStorage.getItem(introSeenKey(tenantId)) !== 'true') {
          // First load after unlock: open the corner panel once so the user
          // sees where the checklist went. Every later load starts collapsed.
          localStorage.setItem(introSeenKey(tenantId), 'true')
          setExpanded(true)
        }
      })
      .catch(() => setStatus(null))
      .finally(() => setLoading(false))
  }, [user?.tenantId, user?.onboardingDismissed]) // eslint-disable-line react-hooks/exhaustive-deps

  // Explicit dismiss (X) and the all-done completion share one exit path.
  const persistDismiss = () => {
    dismissOnboarding().catch(() => { /* non-fatal: server false → widget returns next session */ })
    if (user?.tenantId) localStorage.setItem(dismissKey(user.tenantId), 'true')
    if (user) setUser({ ...user, onboardingDismissed: true })
    setDismissed(true)
    onDismissed()
  }

  const handleDismiss = () => {
    const requiredDone = !!status && CHECKLIST_ITEMS.filter((i) => i.required).every((i) => status[i.key])
    if (!requiredDone) onUnlocked() // dismissing the locked card unlocks the dashboard
    persistDismiss()
  }

  // If the completion Lottie never loads, fall through after 1.5s.
  useEffect(() => {
    if (!completing || celebrationData) return
    const timer = setTimeout(persistDismiss, 1500)
    return () => clearTimeout(timer)
  }, [completing, celebrationData]) // eslint-disable-line react-hooks/exhaustive-deps

  if (dismissed || loading || !status) return null

  const requiredDone = CHECKLIST_ITEMS.filter((i) => i.required).every((i) => status[i.key])
  const doneCount = CHECKLIST_ITEMS.filter((i) => status[i.key]).length

  // ── Mode 2: corner setup guide (required step done, work remaining) ──────
  if (requiredDone) {
    return (
      // Offset past the fixed z-50 sidebar (w-16/w-64) — mirrors app-layout's
      // lg:ml-16 / lg:ml-64 content shift via .sg-corner-wide.
      <div className={`sg-corner${sidebarCollapsed ? '' : ' sg-corner-wide'}`}>
        {expanded ? (
          <div className="sg-panel">
            <div className="sg-panel-header">
              <p className="sg-panel-title">Setup guide</p>
              <span className="sg-panel-count">{doneCount} of {CHECKLIST_ITEMS.length}</span>
              <button className="sg-icon-btn" aria-label="Collapse setup guide" onClick={() => setExpanded(false)}>
                <ChevronDown className="h-4 w-4" />
              </button>
              <button className="sg-icon-btn" aria-label="Dismiss setup guide" onClick={handleDismiss}>
                <X className="h-4 w-4" />
              </button>
            </div>
            {completing ? (
              <div className="sg-complete">
                {!!celebrationData && (
                  <Lottie
                    animationData={celebrationData as any}
                    autoplay={true}
                    loop={false}
                    onComplete={persistDismiss}
                    style={{ width: 140, height: 140 }}
                  />
                )}
                <p className="sg-complete-title">All set!</p>
              </div>
            ) : (
              <div className="sg-rows">
                {CHECKLIST_ITEMS.map((item) => (
                  <SetupGuideRow key={item.key} item={item} done={status[item.key]} onNavigate={() => navigate(item.route)} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <button className="sg-pill" onClick={() => setExpanded(true)}>
            <CheckMark size={16} />
            Setup guide · {doneCount} of {CHECKLIST_ITEMS.length}
          </button>
        )}
      </div>
    )
  }

  // ── Mode 1: locked full-width card (required step incomplete) ────────────
  const requiredItems = CHECKLIST_ITEMS.filter((i) => i.required)
  const optionalItems = CHECKLIST_ITEMS.filter((i) => !i.required)
  const completedRequired = requiredItems.filter((i) => status[i.key]).length
  const totalRequired = requiredItems.length
  const progressPct = Math.round((completedRequired / totalRequired) * 100)

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="relative px-6 py-5 border-b border-border bg-gradient-to-r from-[#0074F4]/8 to-[#D447FB]/8">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold tracking-tight">Get Started with Paver</h2>
            <p className="text-sm text-muted-foreground">
              Complete these steps to unlock your dashboard and start creating estimates.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="mt-0.5 shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Dismiss checklist"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {completedRequired} of {totalRequired} required steps complete
            </span>
            <span className="font-medium text-foreground">{progressPct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-border">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progressPct}%`,
                background: 'linear-gradient(to right, #0074F4, #D447FB)',
              }}
            />
          </div>
        </div>
      </div>

      <div className="divide-y divide-border">
        {requiredItems.map((item) => (
          <ChecklistRow
            key={item.key}
            item={item}
            done={status[item.key]}
            onNavigate={() => navigate(item.route)}
          />
        ))}
      </div>

      {optionalItems.length > 0 && (
        <>
          <div className="px-6 py-2.5 bg-muted/40 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Optional</p>
          </div>
          <div className="divide-y divide-border">
            {optionalItems.map((item) => (
              <ChecklistRow
                key={item.key}
                item={item}
                done={status[item.key]}
                onNavigate={() => navigate(item.route)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** Compact row for the corner panel — same animated icons as the locked card,
 * scaled down (hover to play), with the lucide fallback when Lottie is unavailable. */
function SetupGuideRow({
  item,
  done,
  onNavigate,
}: {
  item: ChecklistItem
  done: boolean
  onNavigate: () => void
}) {
  const lottieRef = useRef<LottieRefCurrentProps>(null)
  const animationData = useLottieData(ANIMATION_URLS[item.key])
  const FallbackIcon = FALLBACK_ICONS[item.key]

  const handleMouseEnter = () => {
    if (!done && animationData) {
      lottieRef.current?.play()
    }
  }

  const handleMouseLeave = () => {
    if (!done && animationData) {
      lottieRef.current?.stop()
      lottieRef.current?.goToAndStop(0, true)
    }
  }

  return (
    <div
      className={`sg-row ${done ? 'sg-row-done' : ''}`}
      onClick={done ? undefined : onNavigate}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {done ? <CheckMark size={16} /> : <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />}
      <span className="sg-row-icon">
        {animationData ? (
          <Lottie
            lottieRef={lottieRef}
            animationData={animationData}
            autoplay={false}
            loop={true}
            style={{ width: 26, height: 26 }}
          />
        ) : (
          <FallbackIcon className="h-4 w-4" style={{ color: done ? '#10b981' : '#0074F4' }} />
        )}
      </span>
      <span className="sg-row-label">{item.label}</span>
      {!done && <ArrowRight className="h-3.5 w-3.5 sg-row-go" />}
    </div>
  )
}

/** Full-size row for the locked card — Lottie icon with lucide fallback. */
function ChecklistRow({
  item,
  done,
  onNavigate,
}: {
  item: ChecklistItem
  done: boolean
  onNavigate: () => void
}) {
  const lottieRef = useRef<LottieRefCurrentProps>(null)
  const animationData = useLottieData(ANIMATION_URLS[item.key])
  const FallbackIcon = FALLBACK_ICONS[item.key]

  const handleMouseEnter = () => {
    if (!done && animationData) {
      lottieRef.current?.play()
    }
  }

  const handleMouseLeave = () => {
    if (!done && animationData) {
      lottieRef.current?.stop()
      lottieRef.current?.goToAndStop(0, true)
    }
  }

  return (
    <div
      className={`flex items-center gap-4 px-6 py-4 transition-colors ${
        done ? '' : 'hover:bg-accent/30 cursor-pointer'
      }`}
      onClick={done ? undefined : onNavigate}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="shrink-0">
        {done ? <CheckMark /> : <Circle className="h-5 w-5 text-muted-foreground/40" />}
      </div>

      <div
        className="shrink-0 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: '#0074F414', padding: 6, lineHeight: 0, width: 56, height: 56 }}
      >
        {animationData ? (
          <Lottie
            lottieRef={lottieRef}
            animationData={animationData}
            autoplay={false}
            loop={true}
            style={{ width: 44, height: 44 }}
          />
        ) : (
          <FallbackIcon className="h-6 w-6" style={{ color: done ? '#10b981' : '#0074F4' }} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
          {item.label}
        </p>
        {!done && (
          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
        )}
      </div>

      {!done && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 gap-1.5 text-primary hover:text-primary hover:bg-primary/10"
          onClick={(e) => {
            e.stopPropagation()
            onNavigate()
          }}
        >
          Go
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}
